import React, { useEffect, useMemo, useState } from 'react'
import I18n from 'i18n'
import { useResearchDashboardGroupAlertsQuery } from '../../../backend/Queries'

interface UserAlert {
  userId: string
  totalAlerts: number
  dateSynced: Date
  dateLastEcg?: Date
}

type AlertTab = 'total' | 'weartime' | 'ecg' | 'questionnaire'

interface AlertDetailRow {
  tab: Exclude<AlertTab, 'total'>
  detail: string
  date: string
}

const alertDotForCount = (count: number): 'red' | 'orange' =>
  count >= 5 ? 'red' : 'orange'

// Sync: blue <= 1 day, orange > 1 day and <= 5 days, red > 5 days, this calculation can be
// done in the backend when there is logic.
const alertDotForSync = (date: Date): 'red' | 'orange' | 'blue' => {
  const daysAgo = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24)
  if (daysAgo > 5) return 'red'
  if (daysAgo > 1) return 'orange'
  return 'blue'
}

const formatDMY = (d: Date): string =>
  `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`

const detailDateToTimestamp = (value: string): number => {
  const dateMatches = value.match(/\b\d{2}-\d{2}-\d{4}\b/g)
  if (dateMatches == null) return 0

  const timestamps = dateMatches
    .map((dmy) => {
      const [dayString, monthString, yearString] = dmy.split('-')
      const day = Number(dayString)
      const month = Number(monthString)
      const year = Number(yearString)
      return new Date(year, month - 1, day).getTime()
    })
    .filter((timestamp) => Number.isFinite(timestamp))

  return timestamps.length > 0 ? Math.max(...timestamps) : 0
}

const AlertDetailsModal: React.FC<{
  alert: UserAlert
  alertDetails: AlertDetailRow[]
  onClose: () => void
}> = ({ alert, alertDetails, onClose }) => {
  const [activeTab, setActiveTab] = useState<AlertTab>('total')

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const allRows = useMemo(
    () => [...alertDetails].sort((a, b) => detailDateToTimestamp(b.date) - detailDateToTimestamp(a.date)),
    [alertDetails]
  )
  const rowsForTab = useMemo(() => {
    if (activeTab === 'total') return allRows
    return allRows.filter((r) => r.tab === activeTab)
  }, [allRows, activeTab])

  const tabs: Array<{ key: AlertTab, label: string }> = [
    { key: 'total', label: I18n.t('components.dashboards.research_dashboard.tabs.total') },
    { key: 'weartime', label: I18n.t('components.dashboards.research_dashboard.tabs.weartime') },
    { key: 'ecg', label: I18n.t('components.dashboards.research_dashboard.tabs.ecg') },
    { key: 'questionnaire', label: I18n.t('components.dashboards.research_dashboard.tabs.questionnaire') }
  ]

  return (
    <div
      className='alert-modal-backdrop'
      role='presentation'
      onMouseDown={(e) => {
        // close only when clicking the backdrop (not inside the panel)
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className='alert-modal'
        role='dialog'
        aria-modal='true'
        aria-label='Alert details'
      >
        <button
          className='alert-modal-close'
          type='button'
          onClick={onClose}
          aria-label='Close'
        >
          ×
        </button>

        <div className='alert-modal-header'>
          <div className='alert-modal-user'>{alert.userId}</div>

          <div className='alert-modal-summary'>
            <div className='alert-modal-summary-row'>
              <span className='alert-label-with-dot'>
                <span
                  className={`alert-dot ${alertDotForCount(alert.totalAlerts)}`}
                  aria-hidden='true'
                />
                <span className='alert-modal-summary-text'>
                  {I18n.t('components.dashboards.research_dashboard.total_alerts_count', { count: alert.totalAlerts })}
                </span>
              </span>
            </div>

            <div className='alert-modal-summary-row'>
              <span className='alert-label-with-dot'>
                <span
                  className={`alert-dot ${alertDotForSync(alert.dateSynced)}`}
                  aria-hidden='true'
                />
                <span className='alert-modal-summary-text'>
                  {I18n.t('components.dashboards.research_dashboard.date_last_synced', { date: formatDMY(alert.dateSynced) })}
                </span>
              </span>
            </div>

            {activeTab === 'ecg' && alert.dateLastEcg != null && (
              <div className='alert-modal-summary-row'>
                <span className='alert-label-with-dot'>
                  <span
                    className={`alert-dot ${alertDotForSync(alert.dateLastEcg)}`}
                    aria-hidden='true'
                  />
                  <span className='alert-modal-summary-text'>
                    {I18n.t('components.dashboards.research_dashboard.date_last_ecg', {
                      date: formatDMY(alert.dateLastEcg)
                    })}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>

        <div
          className='alert-modal-tabs'
          role='tablist'
          aria-label='Alert categories'
        >
          {tabs.map((t) => (
            <button
              key={t.key}
              type='button'
              role='tab'
              aria-selected={activeTab === t.key}
              className={`alert-modal-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className='alert-modal-table-shell'>
          <div className='alert-modal-table-head'>
            <div>{I18n.t('components.dashboards.research_dashboard.alert_details')}</div>
            <div>{I18n.t('components.dashboards.research_dashboard.date')}</div>
          </div>

          <div className='alert-modal-table-body'>
            {rowsForTab.length === 0
              ? (
                <div className='alert-modal-empty'>{I18n.t('components.dashboards.research_dashboard.no_data')}</div>
                )
              : (
                  rowsForTab.map((r, idx) => (
                    <div key={`${r.detail}-${idx}`} className='alert-modal-row'>
                      <div className='alert-modal-cell'>{r.detail}</div>
                      <div className='alert-modal-cell alert-modal-date'>
                        {r.date}
                      </div>
                    </div>
                  ))
                )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const ResearchDashboardGroupOverview: React.FC = () => {
  const [selectedAlert, setSelectedAlert] = useState<UserAlert | null>(null)

  // Fetch alerts from the backend
  const { data, isLoading } = useResearchDashboardGroupAlertsQuery()

  const alerts = data?.alerts ?? []
  const alertDetailsByUser = data?.alertDetailsByUser ?? {}

  if (isLoading) {
    return (
      <div className='research-dashboard-group-overview-wrapper'>
        <h2 className='research-dashboard-group-overview-title'>
          {I18n.t('components.dashboards.research_dashboard.group_overview')}
        </h2>
        <div className='research-dashboard-group-overview'>
          <p>{I18n.t('components.dashboards.research_dashboard.charts.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='research-dashboard-group-overview-wrapper'>
      <h2 className='research-dashboard-group-overview-title'>
        {I18n.t('components.dashboards.research_dashboard.group_overview')}
      </h2>

      <div className='research-dashboard-group-overview'>
        {alerts.length > 0
          ? (
              alerts.map((alert) => (
                <button
                  key={`${alert.userId}-${alert.dateSynced.getTime()}`}
                  type='button'
                  className='alert alert-clickable'
                  onClick={() => setSelectedAlert(alert)}
                  aria-label={`Open alert details for user ${alert.userId}`}
                >
                  <div className='alert-user-id'>{alert.userId}</div>

                  <div className='alert-count-group'>
                    <span className='alert-label-with-dot'>
                      <span
                        className={`alert-dot ${alertDotForCount(alert.totalAlerts)}`}
                        aria-hidden='true'
                      />
                      <span className='alert-count-label'>
                        {I18n.t(
                          'components.dashboards.research_dashboard.total_alerts'
                        )}
                      </span>
                    </span>
                    <span className='alert-count-number'>{alert.totalAlerts}</span>
                  </div>

                  <div className='alert-date-group'>
                    <span className='alert-label-with-dot'>
                      <span
                        className={`alert-dot ${alertDotForSync(alert.dateSynced)}`}
                        aria-hidden='true'
                      />
                      <span className='alert-date-label'>
                        {I18n.t(
                          'components.dashboards.research_dashboard.date_synced'
                        )}
                      </span>
                    </span>

                    <span className='alert-date-value'>
                      {formatDMY(alert.dateSynced)}
                    </span>
                  </div>
                </button>
              ))
            )
          : (
            <p className='no-alerts'>{I18n.t('components.dashboards.research_dashboard.no_alerts')}</p>
            )}
      </div>

      {(selectedAlert != null) && (
        <AlertDetailsModal
          alert={selectedAlert}
          alertDetails={alertDetailsByUser[selectedAlert.userId] ?? []}
          onClose={() => setSelectedAlert(null)}
        />
      )}
    </div>
  )
}

export default ResearchDashboardGroupOverview
